import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Typography, Spin, Alert, Space, Tabs, message } from 'antd';
import { FileTextOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import {
    useGetPrivacyPolicyQuery,
    useGetTermsOfServiceQuery,
    useAcceptMultipleLegalDocumentsMutation,
} from '../../services/nestjs-base-api';
import { LegalDocumentType } from '../../services/nestjs-base-api/types';

const { Title, Text, Paragraph } = Typography;

interface LegalAcceptanceModalProps {
    visible: boolean;
    onAccepted: () => void;
    pendingDocuments: string[];
    jwtToken: string;
    acceptanceMethod?: 'signup' | 'login_prompt' | 'settings';
}

const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({
    visible,
    onAccepted,
    pendingDocuments,
    jwtToken,
    acceptanceMethod = 'login_prompt',
}) => {
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('terms_of_service');

    const needsPrivacy = pendingDocuments.includes(LegalDocumentType.PRIVACY_POLICY);
    const needsTerms = pendingDocuments.includes(LegalDocumentType.TERMS_OF_SERVICE);

    const { data: privacyPolicy, isLoading: loadingPrivacy } = useGetPrivacyPolicyQuery(undefined, {
        skip: !needsPrivacy || !visible,
    });

    const { data: termsOfService, isLoading: loadingTerms } = useGetTermsOfServiceQuery(undefined, {
        skip: !needsTerms || !visible,
    });

    const [acceptDocuments, { isLoading: accepting }] = useAcceptMultipleLegalDocumentsMutation();

    // Set initial tab based on which documents need acceptance
    useEffect(() => {
        if (needsTerms) {
            setActiveTab('terms_of_service');
        } else if (needsPrivacy) {
            setActiveTab('privacy_policy');
        }
    }, [needsTerms, needsPrivacy]);

    // Reset checkboxes when modal opens
    useEffect(() => {
        if (visible) {
            setAcceptedPrivacy(false);
            setAcceptedTerms(false);
        }
    }, [visible]);

    const canProceed =
        (!needsPrivacy || acceptedPrivacy) &&
        (!needsTerms || acceptedTerms);

    const handleAccept = async () => {
        try {
            await acceptDocuments({
                jwtToken,
                documentTypes: pendingDocuments,
                acceptanceMethod,
            }).unwrap();

            message.success('Legal documents accepted successfully');
            onAccepted();
        } catch (error) {
            console.error('Failed to accept legal documents:', error);
            message.error('Failed to accept legal documents. Please try again.');
        }
    };

    const isLoading = loadingPrivacy || loadingTerms;

    const tabItems = [];

    if (needsTerms) {
        tabItems.push({
            key: 'terms_of_service',
            label: (
                <span>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    Terms of Service
                </span>
            ),
            children: (
                <div>
                    {loadingTerms ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    ) : termsOfService?.available ? (
                        <>
                            <div
                                style={{
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    padding: '16px',
                                    backgroundColor: '#fafafa',
                                    borderRadius: 8,
                                    border: '1px solid #d9d9d9',
                                    marginBottom: 16,
                                }}
                            >
                                <ReactMarkdown>{termsOfService.content || ''}</ReactMarkdown>
                            </div>
                            <Text type="secondary">
                                Version {termsOfService.version} - Effective {termsOfService.effectiveDate ? new Date(termsOfService.effectiveDate).toLocaleDateString() : 'N/A'}
                            </Text>
                            <div style={{ marginTop: 16 }}>
                                <Checkbox
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                >
                                    I have read and agree to the Terms of Service
                                </Checkbox>
                            </div>
                        </>
                    ) : (
                        <Alert
                            message="Terms of Service not available"
                            description="Please contact support if this issue persists."
                            type="warning"
                        />
                    )}
                </div>
            ),
        });
    }

    if (needsPrivacy) {
        tabItems.push({
            key: 'privacy_policy',
            label: (
                <span>
                    <SafetyOutlined style={{ marginRight: 8 }} />
                    Privacy Policy
                </span>
            ),
            children: (
                <div>
                    {loadingPrivacy ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    ) : privacyPolicy?.available ? (
                        <>
                            <div
                                style={{
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    padding: '16px',
                                    backgroundColor: '#fafafa',
                                    borderRadius: 8,
                                    border: '1px solid #d9d9d9',
                                    marginBottom: 16,
                                }}
                            >
                                <ReactMarkdown>{privacyPolicy.content || ''}</ReactMarkdown>
                            </div>
                            <Text type="secondary">
                                Version {privacyPolicy.version} - Effective {privacyPolicy.effectiveDate ? new Date(privacyPolicy.effectiveDate).toLocaleDateString() : 'N/A'}
                            </Text>
                            <div style={{ marginTop: 16 }}>
                                <Checkbox
                                    checked={acceptedPrivacy}
                                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                                >
                                    I have read and agree to the Privacy Policy
                                </Checkbox>
                            </div>
                        </>
                    ) : (
                        <Alert
                            message="Privacy Policy not available"
                            description="Please contact support if this issue persists."
                            type="warning"
                        />
                    )}
                </div>
            ),
        });
    }

    return (
        <Modal
            title={
                <Space>
                    <FileTextOutlined style={{ fontSize: 24, color: '#F59E0B' }} />
                    <Title level={4} style={{ margin: 0 }}>Legal Document Updates</Title>
                </Space>
            }
            open={visible}
            closable={false}
            maskClosable={false}
            keyboard={false}
            width={800}
            footer={[
                <Button
                    key="accept"
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleAccept}
                    loading={accepting}
                    disabled={!canProceed || isLoading}
                >
                    Accept and Continue
                </Button>,
            ]}
        >
            <Alert
                message="Action Required"
                description="Please review and accept our updated legal documents to continue using BrandScience.ai."
                type="warning"
                showIcon
                style={{ marginBottom: 24, background: '#fffbeb', border: '1px solid #fde68a' }}
            />

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />

            {pendingDocuments.length > 1 && (
                <Paragraph type="secondary" style={{ marginTop: 16 }}>
                    Please review and accept all {pendingDocuments.length} documents listed above.
                </Paragraph>
            )}
        </Modal>
    );
};

export default LegalAcceptanceModal;
